import type { ZodSchema } from 'zod';
import { getLogger } from '~/lib/logger/logger';

const logger = getLogger('useStorage');

/**
 * Custom hook to retrieve and save data from local or session storage.
 *
 * @param key - The key to save the data under in local or session storage.
 * @param schema - The schema to validate the data against.
 * @param storage - The storage to use.
 * @returns Object to interact with the local or session storage item.
 */
export const useStorage = <T>(
  key: string,
  schema: ZodSchema<T>,
  storage: 'localStorage' | 'sessionStorage' = 'localStorage',
) => {
  const setItem = (value: T) => {
    try {
      if (typeof window === 'undefined') {
        return;
      }
      window[storage].setItem(key, JSON.stringify(value));
    } catch (error) {
      logger.error(`Error saving to ${storage}: ${key}.`, { error });
    }
  };

  const getItem = () => {
    try {
      if (typeof window === 'undefined') {
        return null;
      }
      const item = window[storage].getItem(key);
      const parsedItem = item ? JSON.parse(item) : null;

      if (!parsedItem) {
        return null;
      }

      const result = schema.safeParse(parsedItem);

      if (result.success) {
        return result.data;
      }

      return null;
    } catch (error) {
      logger.error(`Error retrieving from ${storage}: ${key}.`, { error });
      return null;
    }
  };

  const removeItem = () => {
    try {
      if (typeof window === 'undefined') {
        return null;
      }
      window[storage].removeItem(key);
      return key;
    } catch (error) {
      logger.error(`Error clearing ${storage}: ${key}.`, { error });
      return null;
    }
  };

  return { setItem, getItem, removeItem };
};
